/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, November 11th 2021, 10:58:50 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

/**
 * The NestedOne2ManyPopupFormInput should be used for a data schema that has a list of objects as one of its
 * JSON fields. The input provides a listing of current data and ability to add new items via a pop up form
 * that is provided as a render prop. The list should be nested within the schema. For API requests,
 * the data posted contains the nested JSON. This input can be used for data that is not necessary posted
 * to an API.
 */

import React from "react";
import { useControlledInput } from "ui-widgets/forms";
import { OSActionButton } from "ui-widgets";
import { useToggleState } from "ui-widgets/dialogs/useToggleState";
import { FormSubmitBubblingStopper } from "ui-widgets/forms/FormSubmitBubblingStopper";
import {
  APIFormRelatedPopupInputWrapper,
  GenericAPIRelatedFormPopupInputProps,
} from "ui-widgets/forms/input-fields/related/APIFormRelatedPopupInputWrapper";
import { StyledOSDialog } from "ui-widgets/dialogs/GenericDialogs";
import { SimpleListEditor, EditListRenderPropSpec } from "ui-widgets/editors/SimpleListEditor";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import FolderIcon from "@mui/icons-material/Folder";
import DeleteIcon from "@mui/icons-material/Delete";

interface PopUpFormRenderPropSpec<RelatedObject>
  extends Pick<EditListRenderPropSpec<RelatedObject>, "currentEditItem"> {
  onChangeRelatedItem: (item: RelatedObject) => void;
  handleClose: () => void;
}

type NestedOne2ManyPopupFormInputProps<PrimaryObject, RelatedObject> = GenericAPIRelatedFormPopupInputProps<
  PrimaryObject,
  RelatedObject
> & {
  renderRelatedObjectForm: (args: PopUpFormRenderPropSpec<RelatedObject>) => JSX.Element;
};

export const NestedOne2ManyPopupFormInput = <PrimaryObject, RelatedObject>(
  props: NestedOne2ManyPopupFormInputProps<PrimaryObject, RelatedObject>
) => {
  const { open, handleClose, handleOpen } = useToggleState();

  const {
    field: { onChange, value },
  } = useControlledInput<PrimaryObject>(props);

  // Get values on the first render and dont follow the controlled value after that. React will
  // complain about the missing dependency but its intentional
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialItems = React.useMemo(() => value, []);

  // Setting the form field value for this controlled input
  const handleOnUpdateList = React.useCallback((updatedItems: RelatedObject[]) => {
    onChange(updatedItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <APIFormRelatedPopupInputWrapper {...props}>
      <SimpleListEditor
        initialItems={(initialItems as unknown as RelatedObject[]) ?? []}
        onUpdateList={handleOnUpdateList}
      >
        {({ listState: { editableItems }, currentEditItem, updateList, selectItemToEdit, removeItem }) => {
          const onClickAddNew = () => {
            selectItemToEdit(null);
            handleOpen();
          };

          const onClickEdit = (itemIndex: number) => {
            selectItemToEdit(itemIndex);
            handleOpen();
          };

          const onChangeRelatedItem = (item: RelatedObject) => {
            updateList(item);
            handleClose();
          };

          return (
            <>
              <OSActionButton onClick={onClickAddNew} actionStyle="add">
                {`${props.label ?? "Add"}`}
              </OSActionButton>

              <FormSubmitBubblingStopper>
                <StyledOSDialog
                  popUpTitle={props.popUpTitle}
                  popUpDescription={props.popUpDescription}
                  open={open}
                  onClose={handleClose}
                >
                  {props.renderRelatedObjectForm({ currentEditItem, onChangeRelatedItem, handleClose })}
                </StyledOSDialog>
              </FormSubmitBubblingStopper>
              <EditableItemsList
                items={editableItems}
                itemIdProperty={props.itemIdProperty}
                getOptionLabel={props.getOptionLabel}
                onClickEditItem={onClickEdit}
                onClickDeleteItem={removeItem}
              />
            </>
          );
        }}
      </SimpleListEditor>
    </APIFormRelatedPopupInputWrapper>
  );
};

const EditableItemsList = ({
  items,
  itemIdProperty,
  getOptionLabel,
  item_secondary_text = null,
  onClickEditItem,
  onClickDeleteItem,
}) => (
  <React.Fragment>
    <List dense={true}>
      {items.map((item, index) => (
        <ListItem
          key={item?.[itemIdProperty] ? item?.[itemIdProperty].toString() : index} // id Property can be null when a new object is created, so we cater for that
          secondaryAction={
            <React.Fragment>
              <Button onClick={() => onClickEditItem && onClickEditItem(index)}>Edit Item</Button>
              <IconButton edge="end" aria-label="delete" onClick={() => onClickDeleteItem && onClickDeleteItem(index)}>
                <DeleteIcon />
              </IconButton>
            </React.Fragment>
          }
        >
          <ListItemAvatar>
            <Avatar>
              <FolderIcon />
            </Avatar>
          </ListItemAvatar>
          <ListItemText primary={getOptionLabel(item)} secondary={item_secondary_text} />
        </ListItem>
      ))}
    </List>
  </React.Fragment>
);
